import React, { useState } from 'react';
import styled from 'styled-components';
import { formatHoraForDisplay } from '../../utils/dateHelpers';
import {
  detalleDollarLinked, esDollarLinked, formatIsoDay, formatMontoEvento, ESTADO_PENDIENTE,
} from '../../utils/acreditacionesEsperadas';
import { formatNumberForDisplay, parseArgentineNumber } from '../../utils/formatters';
import { ESTADO_ACREDITADA, ESTADO_NO_LLEGO } from '../../services/acreditacionesEsperadasService';

const SectionTitle = styled.div`
  background-color: rgba(26, 40, 62, 0.08);
  padding: 0.5rem 0.9rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.theme.colors.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  span { text-transform: none; font-weight: 500; color: ${props => props.theme.colors.textSecondary}; }
`;

const NotaMarca = styled.p`
  margin: 0;
  padding: 0.45rem 0.9rem;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const EventoRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 130px 170px;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-size: 0.85rem;
  align-items: center;
  &:last-child { border-bottom: none; }

  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;
  }
`;

const TipoBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  background-color: rgba(26, 40, 62, 0.1);
  color: ${props => props.theme.colors.primary};
  justify-self: start;
`;

const Detalle = styled.div`
  small {
    display: block;
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.75rem;
  }
`;

const Monto = styled.span`
  text-align: right;
  font-weight: 600;
  font-family: monospace;
  color: ${props => (props.$negativo ? '#991B1B' : props.theme.colors.primary)};
`;

const Acciones = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const EstadoChip = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  background-color: ${props => ({
    [ESTADO_ACREDITADA]: 'rgba(34, 197, 94, 0.15)',
    [ESTADO_NO_LLEGO]: 'rgba(239, 68, 68, 0.15)',
  }[props.$estado] || 'rgba(245, 158, 11, 0.18)')};
  color: ${props => ({
    [ESTADO_ACREDITADA]: '#065F46',
    [ESTADO_NO_LLEGO]: '#991B1B',
  }[props.$estado] || '#92400E')};
`;

const MarcaButton = styled.button`
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.25rem 0.55rem;
  cursor: pointer;
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.secondary};
  &:hover:not(:disabled) { border-color: ${props => props.theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: wait; }
`;

const ImporteRealForm = styled.form`
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius};
  background-color: rgba(26, 40, 62, 0.05);
  font-size: 0.8rem;
  label { font-weight: 600; color: ${props => props.theme.colors.secondary}; }
  input {
    font-family: monospace;
    font-size: 0.85rem;
    text-align: right;
    width: 11rem;
    padding: 0.3rem 0.5rem;
    border: 1px solid ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius};
  }
  small { flex-basis: 100%; color: ${props => props.theme.colors.textSecondary}; }
  small[role='alert'] { color: #991B1B; }
`;

const Empty = styled.div`
  padding: 0.85rem 0.9rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.textSecondary};
  font-style: italic;
`;

const ESTADO_LABEL = {
  [ESTADO_ACREDITADA]: 'Acreditada',
  [ESTADO_NO_LLEGO]: 'No llegó',
  [ESTADO_PENDIENTE]: 'Sin corroborar',
};

const TIPO_LABEL = { bono: 'bono', cheque: 'cheque', chequePropio: 'ch. propio' };

// Un dollar linked sin importe propio: al marcarlo Acreditada hay que informar los
// pesos que llegaron (el estimado no entra al saldo).
const pideImporteReal = ev => esDollarLinked(ev) && typeof ev.monto !== 'number';

/**
 * Sección del desglose con los eventos esperados del comitente en la moneda
 * activa. La mesa y los comerciales los marcan como acreditados / no llegados; el
 * productor solo ve el estado.
 */
const AcreditacionesEsperadasSection = ({ resumen, moneda, puedeMarcar, onMarcar }) => {
  const [enCurso, setEnCurso] = useState(null);
  const [importeReal, setImporteReal] = useState(null);
  const eventos = resumen?.eventos || [];

  const marcar = async (ev, estado, montoReal) => {
    setEnCurso(ev.eventId);
    try {
      await onMarcar(ev, estado, montoReal);
    } finally {
      setEnCurso(null);
    }
  };

  const abrirImporteReal = (ev) => {
    const estimado = typeof ev.montoEstimado === 'number' ? formatNumberForDisplay(Math.abs(ev.montoEstimado)) : '';
    setImporteReal({ eventId: ev.eventId, valor: estimado, error: null });
  };

  const confirmarImporteReal = async (e, ev) => {
    e.preventDefault();
    const pesos = parseArgentineNumber(importeReal.valor, true);
    if (!(pesos > 0)) {
      setImporteReal(prev => ({ ...prev, error: 'Ingresá los pesos acreditados (mayor a cero).' }));
      return;
    }
    // El importe se escribe en positivo; el signo es el del evento (un pagaré propio debita).
    const signo = ev.montoNominal < 0 ? -1 : 1;
    await marcar(ev, ESTADO_ACREDITADA, Math.round(signo * pesos * 100) / 100);
    setImporteReal(null);
  };

  return (
    <>
      <SectionTitle>
        Acreditaciones esperadas hoy (SGC) — {eventos.length}{' '}
        <span>
          · entran al saldo al marcarlas Acreditada
          {resumen?.fechaCartera ? ` · cartera al ${formatIsoDay(resumen.fechaCartera)}` : ''}
        </span>
      </SectionTitle>
      {puedeMarcar && eventos.length > 0 && (
        <NotaMarca>
          Marcá Acreditada solo lo que llegó después de la apertura (09:30): si ya estaba en el saldo
          inicial, se cuenta dos veces.
        </NotaMarca>
      )}
      {eventos.length === 0 ? (
        <Empty>Sin acreditaciones esperadas para hoy en esta moneda.</Empty>
      ) : eventos.map(ev => (
        <EventoRow key={ev.eventId}>
          <TipoBadge>{TIPO_LABEL[ev.tipo] || ev.tipo}</TipoBadge>
          <Detalle>
            {ev.concepto} — {ev.instrumento}
            {ev.fechaOriginal && <small>Fecha informada {formatIsoDay(ev.fechaOriginal)}, corrida al día hábil.</small>}
            {esDollarLinked(ev) ? <small>{detalleDollarLinked(ev)}</small> : ev.nota && <small>{ev.nota}</small>}
            {ev.marcadoPor && (
              <small>Marcado por {ev.marcadoPor}{ev.marcadoAt ? ` a las ${formatHoraForDisplay(ev.marcadoAt)}` : ''}</small>
            )}
          </Detalle>
          <Monto $negativo={(ev.monto ?? ev.montoEstimado ?? ev.montoNominal ?? 0) < 0}>{formatMontoEvento(ev, moneda)}</Monto>
          <Acciones>
            <EstadoChip $estado={ev.estado}>{ESTADO_LABEL[ev.estado] || ev.estado}</EstadoChip>
            {puedeMarcar && ev.estado !== ESTADO_ACREDITADA && (
              <MarcaButton
                type="button"
                disabled={enCurso === ev.eventId || importeReal?.eventId === ev.eventId}
                onClick={() => (pideImporteReal(ev) ? abrirImporteReal(ev) : marcar(ev, ESTADO_ACREDITADA))}
              >
                Acreditada
              </MarcaButton>
            )}
            {puedeMarcar && ev.estado !== ESTADO_NO_LLEGO && (
              <MarcaButton type="button" disabled={enCurso === ev.eventId} onClick={() => marcar(ev, ESTADO_NO_LLEGO)}>
                No llegó
              </MarcaButton>
            )}
            {puedeMarcar && ev.estado !== ESTADO_PENDIENTE && (
              <MarcaButton type="button" disabled={enCurso === ev.eventId} onClick={() => marcar(ev, ESTADO_PENDIENTE)}>
                Deshacer
              </MarcaButton>
            )}
          </Acciones>
          {importeReal?.eventId === ev.eventId && (
            <ImporteRealForm onSubmit={e => confirmarImporteReal(e, ev)}>
              <label htmlFor={`importe-real-${ev.eventId}`}>Pesos acreditados</label>
              <input
                id={`importe-real-${ev.eventId}`}
                inputMode="decimal"
                autoFocus
                value={importeReal.valor}
                onChange={e => setImporteReal({ ...importeReal, valor: e.target.value, error: null })}
              />
              <MarcaButton type="submit" disabled={enCurso === ev.eventId}>Confirmar</MarcaButton>
              <MarcaButton type="button" disabled={enCurso === ev.eventId} onClick={() => setImporteReal(null)}>
                Cancelar
              </MarcaButton>
              {importeReal.error
                ? <small role="alert">{importeReal.error}</small>
                : <small>Dollar linked: cargá el importe en pesos que figura en la cuenta. Viene precargado el estimado.</small>}
            </ImporteRealForm>
          )}
        </EventoRow>
      ))}
    </>
  );
};

export default AcreditacionesEsperadasSection;
